import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useStatiquesTextes, TexteRiche } from '../hooks/useFormTextes';

const NAV_SECTIONS = [
  { id: 'notre-histoire', label: 'Notre Histoire', short: 'Histoire' },
  { id: 'valeurs', label: 'Nos Valeurs', short: 'Valeurs' },
  { id: 'ambiance', label: 'L\'ambiance Rhône-Amitié', short: 'Ambiance' },
  { id: 'aventures', label: '30 ans d\'aventures', short: 'Aventures' },
  { id: 'retrouvailles', label: 'Retrouvailles 2027', short: 'Retrouvailles' },
  { id: 'equipe', label: 'Notre Équipe', short: 'Équipe' },
  { id: 'departs', label: 'Nos voyageurs', short: 'Voyageurs' },
  { id: 'pourquoi', label: 'Pourquoi nous', short: 'Pourquoi' },
];

const About = () => {
  const T = useStatiquesTextes(); // Iter 770 — textes éditables (Textes du site → Pages du site)
  const [activeSection, setActiveSection] = useState('');
  const [showSticky, setShowSticky] = useState(false);
  const sommaireRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );

    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (sommaireRef.current) {
        const rect = sommaireRef.current.getBoundingClientRect();
        setShowSticky(rect.bottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      const isMobile = window.innerWidth < 1024;
      const offset = isMobile ? 120 : 150;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="about-page" data-testid="about-page">
      {/* Sticky nav - positioned below main header */}
      <div
        className={`fixed top-[68px] lg:top-[100px] left-0 right-0 z-40 bg-white/95 backdrop-blur-md shadow-md border-b border-gray-100 transition-all duration-300 ${showSticky ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}`}
        data-testid="sticky-nav"
      >
        <div className="container-custom">
          <div className="flex items-center gap-1 py-2.5 overflow-x-auto scrollbar-hide">
            <span className="text-[#1E3A5F] font-bold text-sm mr-2 whitespace-nowrap hidden sm:block">Sommaire</span>
            <div className="h-5 w-px bg-gray-300 mr-1 hidden sm:block" />
            {NAV_SECTIONS.map(({ id, short }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  activeSection === id
                    ? 'bg-[#5BC5D4] text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-[#1E3A5F]'
                }`}
                data-testid={`sticky-nav-${id}`}
              >
                {short}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 uppercase tracking-wide" data-testid="about-title">QUI SOMMES-NOUS ?</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            {<TexteRiche texte={T('about_hero_sous_titre')} />}
          </p>
        </div>
      </section>

      {/* Sommaire */}
      <div ref={sommaireRef} className="bg-white border-b border-gray-100 shadow-sm" data-testid="sommaire">
        <div className="container-custom py-5">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-sm font-bold text-[#1E3A5F] uppercase tracking-wider mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-[#5BC5D4]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
              Sur cette page
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {NAV_SECTIONS.map(({ id, label }, i) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="text-left group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F0FAFB] transition-colors"
                  data-testid={`sommaire-${id}`}
                >
                  <span className="w-5 h-5 rounded-full bg-[#5BC5D4]/10 text-[#5BC5D4] text-[10px] font-bold flex items-center justify-center flex-shrink-0 group-hover:bg-[#5BC5D4] group-hover:text-white transition-colors">
                    {i + 1}
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-[#1E3A5F] font-medium transition-colors">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Notre Histoire */}
      <section id="notre-histoire" className="section" data-testid="history-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-[#1E3A5F] text-center">Notre Histoire</h2>
            <div className="space-y-6">
              <div className="card p-8">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  {<TexteRiche texte={T('about_histoire_1')} />}
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  {<TexteRiche texte={T('about_histoire_2')} />}
                </p>
              </div>

              <div className="card p-8 bg-[#F0FAFB] border-l-4 border-[#5BC5D4]">
                <h3 className="text-xl font-bold text-[#1E3A5F] mb-3">Un fonctionnement familial et transparent</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {<TexteRiche texte={T('about_familial')} />}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                    <span className="block text-sm text-gray-500">SIRET</span>
                    <span className="font-bold text-[#1E3A5F] text-sm">50511550100015</span>
                  </div>
                  <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                    <span className="block text-sm text-gray-500">TVA intracommunautaire</span>
                    <span className="font-bold text-[#1E3A5F] text-sm">FR02505115501</span>
                  </div>
                  <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                    <span className="block text-sm text-gray-500">Transparence</span>
                    <span className="font-bold text-[#1E3A5F] text-sm">Tarifs, hôtels, conditions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Valeurs */}
      <section id="valeurs" className="section bg-gray-50" data-testid="values-section">
        <div className="container-custom">
          <h2 className="text-4xl font-bold mb-10 text-[#1E3A5F] text-center">Nos Valeurs</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card p-6 border-t-4 border-[#5BC5D4]">
              <div className="w-12 h-12 rounded-full bg-[#5BC5D4]/10 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#5BC5D4]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1E3A5F]">Confiance</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {<TexteRiche texte={T('about_confiance')} />}
              </p>
            </div>

            <div className="card p-6 border-t-4 border-[#79c9ea]">
              <div className="w-12 h-12 rounded-full bg-[#79c9ea]/10 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#79c9ea]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1E3A5F]">Sécurité</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {<TexteRiche texte={T('about_securite')} />}
              </p>
            </div>

            <div className="card p-6 border-t-4 border-[#27AE60]">
              <div className="w-12 h-12 rounded-full bg-[#27AE60]/10 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#27AE60]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1E3A5F]">Souplesse</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {<TexteRiche texte={T('about_souplesse')} />}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* L'ambiance Rhône-Amitié */}
      <section id="ambiance" className="section" data-testid="philosophie-section">
        <div className="container-custom">
          {/* Bannière courte */}
          <div className="bg-[#1E3A5F] text-white rounded-2xl p-8 md:p-10 mb-10 max-w-4xl mx-auto text-center shadow-xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-5">L'ambiance Rhône-Amitié</h2>
            <p className="text-lg md:text-xl leading-relaxed opacity-95">
              {<TexteRiche texte={T('about_ambiance_banniere')} />}
            </p>
          </div>

          {/* Texte principal */}
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Intro */}
            <div className="card p-8">
              <p className="text-gray-700 leading-relaxed text-lg italic border-l-4 border-[#5BC5D4] pl-5">
                {<TexteRiche texte={T('about_ambiance_intro')} />}
              </p>
            </div>

            {/* L'ambiance */}
            <div className="card p-8">
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">L'ambiance avant tout</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                {<TexteRiche texte={T('about_ambiance_1')} />}
              </p>
            </div>

            {/* Les générations */}
            <div className="card p-8">
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">Des générations qui se croisent</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                {<TexteRiche texte={T('about_generations')} />}
              </p>
            </div>

            {/* Le derby */}
            <div className="card p-8">
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">La trêve du voyage</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                {<TexteRiche texte={T('about_treve')} />}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-600 font-medium mb-1">Dehors...</p>
                  <p className="text-gray-700 text-sm">Chacun son club.</p>
                </div>
                <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4] rounded-xl p-4">
                  <p className="text-sm text-[#1E3A5F] font-medium mb-1">Dedans...</p>
                  <p className="text-gray-700 text-sm">{<TexteRiche texte={T('about_treve_dedans')} />}</p>
                </div>
              </div>
            </div>

            {/* Ouverture */}
            <div className="card p-8">
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">Ouverte à tous</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Notre association n'est pas une boîte fermée, ni un club réservé. Elle est ouverte à tous :
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-[#5BC5D4] font-bold mt-1">—</span>
                  <span>à ceux qui connaissent tout le monde,</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-[#5BC5D4] font-bold mt-1">—</span>
                  <span>à ceux qui viennent seuls la première fois,</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-[#5BC5D4] font-bold mt-1">—</span>
                  <span>à ceux qui ont des racines proches, lointaines, ou même difficiles à nommer,</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-[#5BC5D4] font-bold mt-1">—</span>
                  <span>et à tous ceux qui veulent simplement partir ensemble, dans une ambiance chaleureuse, humaine, sans jugement.</span>
                </li>
              </ul>
            </div>

            {/* Les bons moments */}
            <div className="card p-8">
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">On laisse la vie s'occuper des bons moments</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                {<TexteRiche texte={T('about_bons_moments')} />}
              </p>
              <div className="bg-[#1E3A5F] text-white rounded-xl p-5 text-center">
                <p className="text-lg leading-relaxed italic">
                  « On reviendra, hein ? »
                </p>
              </div>
            </div>

            {/* Conclusion */}
            <div className="card p-8">
              <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2C5282] text-white rounded-xl p-6">
                <p className="leading-relaxed text-base mb-4">
                  {<TexteRiche texte={T('about_conclusion')} />}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* 30 ans d'aventures partagées */}
      <section id="aventures" className="section bg-gradient-to-b from-[#e8f4f8] to-white" data-testid="past-destinations-section">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4 text-[#1E3A5F]">30 ans d'aventures partagées</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              {<TexteRiche texte={T('about_aventures')} />}
            </p>

            {/* Mosaic photos souvenirs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="souvenir-photos">
              {[
                { src: "https://images.unsplash.com/photo-1664472042270-26ba92fd830f?w=400&h=300&fit=crop", label: "Venise" },
                { src: "https://images.unsplash.com/photo-1675201449998-0c188b674ece?w=400&h=300&fit=crop", label: "Rome" },
                { src: "https://images.unsplash.com/photo-1628790891451-024e881c49e2?w=400&h=300&fit=crop", label: "Marrakech" },
                { src: "https://images.unsplash.com/photo-1533929736458-ca588d08c8be?w=400&h=300&fit=crop", label: "Londres" },
                { src: "https://images.unsplash.com/photo-1669205342327-e01c676bc723?w=400&h=300&fit=crop", label: "Rép. Dominicaine" },
                { src: "https://images.unsplash.com/photo-1775129841086-95e0b1ada71d?w=400&h=300&fit=crop", label: "Croisière Nil" },
                { src: "https://images.unsplash.com/photo-1604145173766-3a763061bc5b?w=400&h=300&fit=crop", label: "Dubaï" },
                { src: "https://images.unsplash.com/photo-1677489611789-bf1d7d2627cb?w=400&h=300&fit=crop", label: "Tenerife" },
              ].map((photo, i) => (
                <div key={i} className="relative group overflow-hidden rounded-xl shadow-md">
                  <img
                    src={photo.src}
                    alt={photo.label}
                    className="w-full h-36 sm:h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <span className="absolute bottom-2 left-3 text-white text-sm font-bold drop-shadow-lg">{photo.label}</span>
                </div>
              ))}
            </div>

            {/* Liste destinations avec drapeaux */}
            <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="past-destinations-list">
              {[
                { name: "Rép. Dominicaine", flag: "do" },
                { name: "Venise", flag: "it" },
                { name: "Gênes", flag: "it" },
                { name: "Savone", flag: "it" },
                { name: "Florence", flag: "it" },
                { name: "Rome", flag: "it" },
                { name: "Naples", flag: "it" },
                { name: "Cagliari", flag: "it" },
                { name: "Palerme", flag: "it" },
                { name: "Malaga", flag: "es" },
                { name: "Londres", flag: "gb" },
                { name: "Algarve", flag: "pt" },
                { name: "Madère", flag: "pt" },
                { name: "Tenerife", flag: "es" },
                { name: "Croisière Nil", flag: "eg" },
                { name: "Djerba", flag: "tn" },
                { name: "Marrakech", flag: "ma" },
                { name: "Agadir", flag: "ma" },
                { name: "Bahreïn", flag: "bh" },
                { name: "Qatar", flag: "qa" },
                { name: "Oman", flag: "om" },
                { name: "Abu Dhabi", flag: "ae" },
                { name: "Dubaï", flag: "ae" },
              ].map((dest, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm text-[#1E3A5F] font-medium shadow-sm hover:shadow-md hover:border-[#5BC5D4]/40 transition-all"
                >
                  <img
                    src={`https://flagcdn.com/w20/${dest.flag}.png`}
                    srcSet={`https://flagcdn.com/w40/${dest.flag}.png 2x`}
                    alt={dest.flag}
                    className="w-5 h-3.5 object-cover rounded-sm"
                  />
                  {dest.name}
                </span>
              ))}
            </div>
            <p className="text-[#5BC5D4] font-bold text-lg mb-0">23 destinations — et des milliers de souvenirs</p>
          </div>
        </div>
      </section>

      {/* Retrouvailles 2027 */}
      <section id="retrouvailles" className="section bg-white" data-testid="retrouvailles-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="card p-8 sm:p-10 bg-gradient-to-br from-[#1E3A5F] to-[#2a5280] text-white text-center rounded-2xl shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#5BC5D4]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#5BC5D4]/10 rounded-full translate-y-1/2 -translate-x-1/2" />
              <div className="relative z-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">Retrouvailles 2027</h2>
                <p className="text-lg text-blue-100 leading-relaxed mb-4">
                  {<TexteRiche texte={T('about_retrouvailles_1')} />}
                </p>
                <p className="text-blue-100 leading-relaxed mb-6">
                  {<TexteRiche texte={T('about_retrouvailles_2')} />}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="mailto:rhoneamitie@gmail.com?subject=Retrouvailles%2030%20ans%20-%20Mon%20souvenir&body=Bonjour%2C%0A%0AVoici%20mon%20souvenir%20de%20voyage%20avec%20Rh%C3%B4ne%20Amiti%C3%A9%20Voyages%20%3A%0A%0A"
                    className="inline-flex items-center gap-2 bg-white text-[#1E3A5F] font-bold px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                    data-testid="retrouvailles-email-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    Racontez-nous par email
                  </a>
                  <a
                    href="https://wa.me/33660416218?text=Bonjour%20!%20Je%20souhaite%20partager%20un%20souvenir%20de%20voyage%20pour%20les%2030%20ans%20de%20Rh%C3%B4ne%20Amiti%C3%A9%20Voyages%20%3A"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                    data-testid="retrouvailles-whatsapp-btn"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.604-1.208A11.925 11.925 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-2.168 0-4.174-.683-5.822-1.846l-.418-.275-2.73.716.729-2.663-.302-.444A9.796 9.796 0 012.182 12c0-5.423 4.395-9.818 9.818-9.818 5.423 0 9.818 4.395 9.818 9.818 0 5.423-4.395 9.818-9.818 9.818z"/></svg>
                    Racontez-nous par WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notre équipe */}
      <section id="equipe" className="section bg-gray-50" data-testid="team-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-[#1E3A5F] text-center">Une équipe dédiée, de A à Z</h2>
            <div className="card p-8">
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Notre équipe s'occupe <strong>personnellement de l'organisation de A à Z</strong>, ce qui inclut :
                </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      'La conception des voyages',
                      'La communication (WhatsApp, mails)',
                      'Le suivi des inscriptions',
                      'La gestion des paiements et échéanciers',
                      'Le lien avec les partenaires (hôtels, transports, guides)',
                      'L\'accompagnement pour les formalités (passeport, visa, vaccins)',
                      'L\'accompagnement sur place, du départ au retour',
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-[#5BC5D4] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 bg-green-50 rounded-xl p-4">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {<TexteRiche texte={T('about_equipe_encadre')} />}
                    </p>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Une association ouverte à toute la France */}
      <section id="departs" className="section bg-gray-50" data-testid="departure-section">
        <div className="container-custom">
          <h2 className="text-4xl font-bold mb-8 text-[#1E3A5F] text-center">Une association ouverte à toute la France</h2>
          <div className="max-w-3xl mx-auto">
            <div className="card p-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                {<TexteRiche texte={T('about_france_1')} />}
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                {<TexteRiche texte={T('about_france_2')} />}
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                {<TexteRiche texte={T('about_france_3')} />}
              </p>
              <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4] rounded-xl p-4 mt-4">
                <p className="text-sm text-[#1E3A5F] font-medium text-center">
                  {<TexteRiche texte={T('about_france_encadre')} />}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi voyager avec nous - grille d'icônes */}
      <section id="pourquoi" className="section bg-[#F0FAFB]" data-testid="why-section">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-[#1E3A5F] text-center">Pourquoi voyager avec nous ?</h2>
          <p className="text-center text-gray-600 mb-8">Ce que nos participants apprécient particulièrement</p>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>, title: 'Presque 30 ans d\'expérience', desc: 'Un savoir-faire éprouvé depuis 1997' },
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>, title: 'Ambiance conviviale et familiale', desc: 'Des voyages où l\'on se sent en famille' },
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>, title: 'Transparence totale', desc: 'Tarifs, hôtels, compagnies, conditions : tout est clair' },
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>, title: 'Souplesse sur les paiements', desc: 'Échelonnement possible, multiples modes de paiement' },
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>, title: 'Accompagnement administratif', desc: 'Aide pour passeport, visa, vaccins, formulaires' },
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>, title: 'WhatsApp comme canal direct', desc: 'Réactif, pratique, tout sur votre téléphone' },
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>, title: 'Rapport qualité-prix', desc: 'Souvent décrit comme difficile à égaler' },
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>, title: 'Une équipe dédiée', desc: 'Organisation de A à Z par notre équipe expérimentée' },
                { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>, title: 'Assurance multirisque', desc: 'Protection complète pour voyager sereinement' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm flex items-start gap-3">
                  <span className="text-[#5BC5D4] flex-shrink-0 mt-0.5">{item.icon}</span>
                  <div>
                    <h3 className="font-bold text-[#1E3A5F] text-sm">{item.title}</h3>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="section bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white" data-testid="whatsapp-cta">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-3">Notre outil de communication privilégié</h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            {<TexteRiche texte={T('about_whatsapp')} />}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white text-[#25D366] px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-50 transition shadow-lg"
              data-testid="whatsapp-main-btn"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Écrivez-nous sur WhatsApp
            </a>
            <a href="mailto:rhoneamitie@gmail.com" className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white/10 transition"
              data-testid="email-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              rhoneamitie@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#5BC5D4] to-[#3BA4B3] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4">Prêt à rejoindre l'aventure ?</h2>
          <p className="text-xl mb-8 opacity-90">Découvrez nos voyages et réservez dès maintenant !</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/voyages" onClick={() => window.scrollTo(0, 0)} data-testid="see-trips-btn" className="btn bg-white text-[#5BC5D4] hover:bg-gray-100">
              Voir nos voyages
            </Link>
            <Link to="/inscription" onClick={() => window.scrollTo(0, 0)} className="btn btn-outline border-white text-white hover:bg-white hover:text-[#5BC5D4]">
              Comment s'inscrire
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
